import { Navigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useAccessControl, type FeatureKey } from '@/hooks/useAccessControl';
import { useEffectiveAccess } from '@/hooks/useEffectiveAccess';
import { Loader2 } from 'lucide-react';

interface ProtectedRouteProps {
  children: React.ReactNode;
  /** Engine-based feature access check. */
  featureKey?: FeatureKey;
  requireAuth?: boolean;
}

/**
 * Layered route guard.
 *
 *   Schicht A — Identity Gate  (auth + account_status + verification + beta)
 *               via useEffectiveAccess.identityGate — the single source of
 *               truth for "is this user allowed in the app at all?".
 *
 *   Schicht B — Access Gate    (feature key)
 *               Feature keys go through the central access engine.
 *
 * Identity decisions always run first so the redirect target is
 * deterministic and never contradicts BetaGate.
 */
const ProtectedRoute = ({ children, featureKey, requireAuth = true }: ProtectedRouteProps) => {
  const { user, loading: authLoading } = useAuth();
  const { canAccessFeature, loading: acLoading } = useAccessControl();
  const { identityGate, loading: accessLoading } = useEffectiveAccess();

  const loading = authLoading || (!!user && (acLoading || accessLoading));

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  // ── Schicht A — Identity Gate ─────────────────────────────────
  if (requireAuth && !user) {
    return <Navigate to="/auth" replace />;
  }

  // Block suspended/rejected/pending/unverified/no_beta users from
  // protected routes. BetaGate already redirects globally, but
  // ProtectedRoute makes it explicit per route in case BetaGate is
  // bypassed (e.g. nested under a public shell).
  if (user && !identityGate.hasAppAccess) {
    return <Navigate to="/beta-status" replace />;
  }

  // ── Schicht B — Access Gate ───────────────────────────────────
  if (featureKey && !canAccessFeature(featureKey).allowed) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
